import { useSubscription } from '@apollo/client';
import { useEffect, useState } from 'react';

import useMe from '@hooks/useMe';
import {
  MessageUserTypingDocument,
  MessageUserTypingSubscription,
  MessageUserTypingSubscriptionVariables
} from '@typing/Generated';

type Typer = NonNullable<MessageUserTypingSubscription['messageUserTyping']>;

type Args = {
  chatId: string;
};

const useSubscribeToMessageTypers = ({ chatId }: Args) => {
  const me = useMe();
  const [typers, setTypers] = useState<Typer[]>([]);
  const [timesAdded, setTimesAdded] = useState<{ [id: string]: Date }>({});
  const typingDuration = 1000;
  const checkIntervalDuration = 500;

  useSubscription<MessageUserTypingSubscription, MessageUserTypingSubscriptionVariables>(MessageUserTypingDocument, {
    onData: ({ data }) => {
      // If there is a user in the data
      if (data.data?.messageUserTyping?.user) {
        // If the user is not me
        if (me?.id !== data.data?.messageUserTyping?.user.id) {
          // If the user is not already a typer, add them.
          if (!typers.some(typer => typer.user && typer.user.id === data.data?.messageUserTyping?.user?.id)) {
            const newTypers = [...typers, data.data?.messageUserTyping];
            setTypers(newTypers);
          }
          // Update the time when they last typed
          const newTimesAdded = { ...timesAdded };
          newTimesAdded[data.data.messageUserTyping.user.id] = new Date();
          setTimesAdded(newTimesAdded);
        }
      }
    },
    variables: { chatId }
  });

  useEffect(() => {
    if (typers.length > 0) {
      const interval = setInterval(() => {
        const newTypers: Typer[] = [];
        typers.forEach(typer => {
          if (typer.user && timesAdded[typer.user.id].getTime() > new Date().getTime() - typingDuration) {
            newTypers.push(typer);
          }
        });
        setTypers(newTypers);
      }, checkIntervalDuration);

      return () => {
        clearInterval(interval);
      };
    }
  }, [timesAdded, typers]);

  return typers;
};

export default useSubscribeToMessageTypers;
